import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Sparkles,
  Mail,
  Clock,
  MessageSquare,
  Lightbulb,
  AlertTriangle,
  Target,
  Send,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Copy,
  Check
} from 'lucide-react';

const URGENCY_CONFIG = {
  immediate: { color: 'bg-red-100 text-red-800', label: 'Immediate' },
  within_24h: { color: 'bg-amber-100 text-amber-800', label: 'Within 24h' },
  within_week: { color: 'bg-blue-100 text-blue-800', label: 'This Week' },
  not_urgent: { color: 'bg-gray-100 text-gray-800', label: 'Not Urgent' }
};

const PRIORITY_CONFIG = {
  high: { color: 'bg-red-100 text-red-800 border-red-200', icon: '🔴' },
  medium: { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: '🟡' },
  low: { color: 'bg-green-100 text-green-800 border-green-200', icon: '🟢' }
};

export default function AIRecommendations({ userId, onSendEmail }) {
  const [loading, setLoading] = useState(true);
  const [recommendations, setRecommendations] = useState(null);
  const [context, setContext] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [copiedPoints, setCopiedPoints] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  useEffect(() => {
    if (userId) {
      fetchRecommendations();
    }
  }, [userId]);
  
  const fetchRecommendations = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`/api/admin/ai/recommendations/${userId}`);
      setRecommendations(response.data.recommendations);
      setContext(response.data.context);
    } catch (error) {
      console.error('Failed to fetch AI recommendations:', error);
      toast.error('Failed to load AI recommendations');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const copyTalkingPoints = () => {
    if (recommendations?.talkingPoints) {
      const text = recommendations.talkingPoints.map((p, i) => `${i + 1}. ${p}`).join('\n');
      navigator.clipboard.writeText(text);
      setCopiedPoints(true);
      toast.success('Talking points copied!');
      setTimeout(() => setCopiedPoints(false), 2000);
    }
  };
  
  const handleSendRecommendedEmail = () => {
    if (recommendations?.recommendedEmailTemplate?.templateName && onSendEmail) {
      onSendEmail(recommendations.recommendedEmailTemplate.templateName);
    }
  };
  
  if (loading) {
    return (
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary animate-pulse" />
            <Skeleton className="h-5 w-40" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }
  
  if (!recommendations) {
    return (
      <Card className="border-muted">
        <CardContent className="py-8 text-center text-muted-foreground">
          <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>Unable to generate recommendations</p>
          <Button variant="ghost" size="sm" onClick={fetchRecommendations} className="mt-2">
            <RefreshCw className="w-4 h-4 mr-1" /> Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  const urgency = URGENCY_CONFIG[recommendations.followUpTiming?.urgency] || URGENCY_CONFIG.within_week;
  const priority = PRIORITY_CONFIG[recommendations.overallPriority] || PRIORITY_CONFIG.medium;
  
  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent" data-testid="ai-recommendations">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-primary" />
            AI Recommendations
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge className={priority.color}>
              {priority.icon} {recommendations.overallPriority?.toUpperCase()} Priority
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={fetchRecommendations}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <CardDescription>
          Personalized outreach suggestions based on participant's journey
        </CardDescription>
      </CardHeader>
      
      {expanded && (
        <CardContent className="space-y-5">
          {/* Recommended Email Template */}
          <div className="p-4 bg-card rounded-lg border border-border">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium mb-1">Recommended Email</h4>
                {recommendations.recommendedEmailTemplate?.templateName ? (
                  <>
                    <p className="text-sm font-medium text-primary">
                      {recommendations.recommendedEmailTemplate.templateName}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {recommendations.recommendedEmailTemplate.reason}
                    </p>
                    {onSendEmail && (
                      <Button 
                        size="sm" 
                        className="mt-3"
                        onClick={handleSendRecommendedEmail}
                        data-testid="send-recommended-email"
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Send This Email
                      </Button>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No email recommendation at this time. Consider personal outreach.
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Follow-up Timing */}
          <div className="p-4 bg-card rounded-lg border border-border">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium mb-1">Follow-up Timing</h4>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={urgency.color}>
                    {urgency.label}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Recommended within {recommendations.followUpTiming?.recommendedDays || 3} days
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {recommendations.followUpTiming?.reason}
                </p>
              </div>
            </div>
          </div>
          
          {/* Talking Points */}
          <div className="p-4 bg-card rounded-lg border border-border">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                </div>
                <h4 className="font-medium">Talking Points for Outreach</h4>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={copyTalkingPoints}
              >
                {copiedPoints ? (
                  <Check className="w-4 h-4 mr-1 text-success" />
                ) : (
                  <Copy className="w-4 h-4 mr-1" />
                )}
                Copy
              </Button>
            </div>
            <ul className="space-y-2">
              {recommendations.talkingPoints?.map((point, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                    {idx + 1}
                  </span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Key Insights */}
          {recommendations.keyInsights?.length > 0 && (
            <div className="p-4 bg-card rounded-lg border border-border">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="w-5 h-5 text-purple-600" />
                </div>
                <h4 className="font-medium">Key Insights</h4>
              </div>
              <ul className="space-y-2">
                {recommendations.keyInsights.map((insight, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <span className="text-purple-500">•</span>
                    <span>{insight}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Risk Factors */}
          {recommendations.riskFactors?.length > 0 && (
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h4 className="font-medium text-red-800">Risk Factors</h4>
              </div>
              <ul className="space-y-2">
                {recommendations.riskFactors.map((risk, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-red-700">
                    <span>⚠️</span>
                    <span>{risk}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Recommended Next Action */}
          <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Target className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-medium text-emerald-800 mb-1">Recommended Next Action</h4>
                <p className="text-sm text-emerald-700">
                  {recommendations.recommendedNextAction}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
